import React from 'react';
import { useTheme } from '../context/ThemeContext';
import { Phone, Mail, MapPin, Facebook, Instagram, Linkedin, ArrowUp } from 'lucide-react';

const Footer = () => {
  const { theme } = useTheme();

  const scrollToTop = () => {
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const quickLinks = [
    { label: 'About Us', href: '#about' },
    { label: 'Services', href: '#services' },
    { label: 'Process', href: '#process' },
    { label: 'Why Us', href: '#why-us' },
    { label: 'Projects', href: '#projects' },
  ];

  const services = [
    'Roof Painting',
    'Roof Cleaning',
    'Roof Recoating',
    'Roof Repairs',
    'Commercial Painting',
    'Residential Painting',
  ];

  return (
    <footer 
      id="contact"
      className="bg-slate-950 text-white"
      data-testid="footer"
    >
      <div className={theme.layout.container}>
        {/* Main Footer */}
        <div className="py-16 grid md:grid-cols-2 lg:grid-cols-4 gap-12">
          {/* Brand */}
          <div>
            {/* Text Logo */}
            <div className="flex items-center mb-6">
              <span className="bg-[#ff5218] text-white font-oswald font-black text-xl px-2 py-1">Sydney</span>
              <span className="font-oswald font-bold text-xl text-white ml-1">Roof Painters</span>
            </div>
            <p className="text-slate-400 text-sm leading-relaxed mb-6">
              Sydney Roof Painters is defined by expertise, premium-quality materials, and a personalised approach that sets us apart. Your vision is our canvas.
            </p>
            {/* Social Icons */}
            <div className="flex gap-3">
              {[Facebook, Instagram, Linkedin].map((Icon, index) => (
                <a 
                  key={index}
                  href="#" 
                  className="w-10 h-10 flex items-center justify-center transition-colors bg-slate-800 hover:bg-[#ff5218] text-white"
                  data-testid={`social-${index}`}
                >
                  <Icon className="w-5 h-5" />
                </a>
              ))}
            </div>
          </div>

          {/* Quick Links */}
          <div>
            <h4 className={`${theme.typography.headingFont} text-lg mb-6 font-semibold uppercase tracking-wider text-[#ff5218]`}>
              Quick Links
            </h4>
            <ul className="space-y-3">
              {quickLinks.map((link) => (
                <li key={link.label}>
                  <a
                    href={link.href}
                    className="text-slate-400 hover:text-white transition-colors text-sm flex items-center gap-2"
                  >
                    <span className="w-1.5 h-1.5 bg-[#ff5218] rounded-full"></span>
                    {link.label}
                  </a>
                </li>
              ))}
            </ul>
          </div>

          {/* Services */}
          <div>
            <h4 className={`${theme.typography.headingFont} text-lg mb-6 font-semibold uppercase tracking-wider text-[#ff5218]`}>
              Our Services
            </h4>
            <ul className="space-y-3">
              {services.map((service) => (
                <li key={service}>
                  <span className="text-slate-400 text-sm flex items-center gap-2">
                    <span className="w-1.5 h-1.5 bg-[#ff5218] rounded-full"></span>
                    {service}
                  </span>
                </li>
              ))}
            </ul>
          </div>

          {/* Contact Info */}
          <div>
            <h4 className={`${theme.typography.headingFont} text-lg mb-6 font-semibold uppercase tracking-wider text-[#ff5218]`}>
              Contact Us
            </h4>
            <ul className="space-y-4">
              <li>
                <a href="tel:1300304599" className="flex items-center gap-3 text-slate-400 hover:text-white transition-colors">
                  <div className="w-10 h-10 flex items-center justify-center bg-[#ff5218] text-white">
                    <Phone className="w-5 h-5" />
                  </div>
                  <div>
                    <p className="text-xs text-slate-500">Call Us</p>
                    <p className="font-semibold text-white">1300 304 599</p>
                  </div>
                </a>
              </li>
              <li>
                <a href="mailto:support@asapsydneypainters.com.au" className="flex items-center gap-3 text-slate-400 hover:text-white transition-colors">
                  <div className="w-10 h-10 flex items-center justify-center bg-[#ff5218] text-white">
                    <Mail className="w-5 h-5" />
                  </div>
                  <div>
                    <p className="text-xs text-slate-500">Email Us</p>
                    <p className="font-semibold text-white text-sm">support@asapsydneypainters.com.au</p>
                  </div>
                </a>
              </li>
              <li className="flex items-center gap-3">
                <div className="w-10 h-10 flex items-center justify-center bg-[#ff5218] text-white">
                  <MapPin className="w-5 h-5" />
                </div>
                <div>
                  <p className="text-xs text-slate-500">Service Area</p>
                  <p className="font-semibold text-white">All Sydney</p>
                </div>
              </li>
            </ul>
          </div>
        </div>

        {/* Bottom Bar */}
        <div className="py-6 border-t border-slate-800 flex flex-col md:flex-row justify-between items-center gap-4">
          <p className="text-slate-500 text-sm">
            © 2024 Sydney Roof Painters. All rights reserved.
          </p>
          <button
            onClick={scrollToTop}
            className="flex items-center gap-2 text-sm font-medium text-[#ff5218] hover:text-[#e54a15] transition-colors"
          >
            <span>Back to Top</span>
            <ArrowUp className="w-4 h-4" />
          </button>
        </div>
      </div>
    </footer>
  );
};

export default Footer;
